import { useState, useMemo, useId, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  Home,
  ChevronDown,
  ChevronRight,
  Loader2,
  X,
  Bed,
  DollarSign,
  Pencil,
  Trash2,
  Info,
  SquarePen,
  Eye,
} from 'lucide-react';
import {
  useProperties,
  useCreateProperty,
  useCreateUnit,
  useUnits,
  useUpdateProperty,
  useDeleteProperty,
  useUpdateUnit,
  useDeleteUnit,
} from '@/hooks/api/useProperties';
import { useUpdateHousekeepingStatus } from '@/hooks/api/useOperations';
import { useOnboardingStatus, useSubmitForReview } from '@/hooks/api/useOnboarding';
import { useAuth } from '@/hooks/useAuth';
import { UserRole, PropertyReviewStatus } from '@pms/shared';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { ReviewStatusBadge } from '@/components/ui/ReviewStatusBadge';
import { Send, MessageSquareWarning, CheckCircle2, Loader2 as Spinner } from 'lucide-react';
import type { UpdatePropertyPayload, UpdateUnitPayload } from '@/services/api/properties.api';

// ── Constants ────────────────────────────────────────────────────────────────

const UNIT_TYPES = [
  { value: 'studio', label: 'Studio' },
  { value: '1bed', label: '1 Bedroom' },
  { value: '2bed', label: '2 Bedrooms' },
  { value: '3bed', label: '3 Bedrooms' },
  { value: 'penthouse', label: 'Penthouse' },
];

const HOUSEKEEPING_STATUS_LABELS: Record<string, string> = {
  clean: 'Clean',
  dirty: 'Dirty',
  inspected: 'Inspected',
  occupied: 'Occupied',
  maintenance: 'Maintenance',
  out_of_order: 'Out of Order',
};

const HOUSEKEEPING_STATUS_STYLES: Record<string, string> = {
  clean: 'bg-green-100 text-green-700',
  dirty: 'bg-red-100 text-red-700',
  inspected: 'bg-blue-100 text-blue-700',
  occupied: 'bg-blue-100 text-blue-700',
  maintenance: 'bg-orange-100 text-orange-700',
  out_of_order: 'bg-gray-100 text-gray-600',
};

const HOUSEKEEPING_TRANSITIONS: Record<string, { label: string; next: string }> = {
  dirty: { label: 'Mark Clean', next: 'clean' },
  clean: { label: 'Inspect', next: 'inspected' },
  inspected: { label: 'Mark Occupied', next: 'occupied' },
  occupied: { label: 'Mark Dirty', next: 'dirty' },
  maintenance: { label: 'Mark Clean', next: 'clean' },
  out_of_order: { label: 'Mark Clean', next: 'clean' },
};

const TRANSITION_BUTTON_STYLES: Record<string, string> = {
  clean: 'border-green-200 text-green-600 hover:bg-green-50',
  inspected: 'border-blue-200 text-blue-600 hover:bg-blue-50',
  occupied: 'border-blue-200 text-blue-600 hover:bg-blue-50',
  dirty: 'border-red-200 text-red-600 hover:bg-red-50',
};

// Shared input class
const INPUT_CLASS =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

// ── Shared SlideOver Shell ───────────────────────────────────────────────────

function SlideOverShell({
  isOpen,
  onClose,
  headingId,
  title,
  subtitle,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  headingId: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" aria-hidden="true" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 id={headingId} className="text-base font-semibold text-gray-900">
              {title}
            </h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </aside>
    </>
  );
}

// ── Add Property Slide-over ──────────────────────────────────────────────────

interface PropertyFormData {
  name: string;
  city: string;
  address: string;
  country: string;
  description: string;
}

const EMPTY_PROPERTY_FORM: PropertyFormData = {
  name: '',
  city: '',
  address: '',
  country: '',
  description: '',
};

function AddPropertySlideOver({
  isOpen,
  onClose,
  ownerId,
}: {
  isOpen: boolean;
  onClose: () => void;
  ownerId: string;
}) {
  const headingId = useId();
  const [form, setForm] = useState<PropertyFormData>(EMPTY_PROPERTY_FORM);
  const createMutation = useCreateProperty();

  function set<K extends keyof PropertyFormData>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate(
      {
        name: form.name.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        country: form.country.trim(),
        ownerId,
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
      },
      {
        onSuccess: () => {
          setForm(EMPTY_PROPERTY_FORM);
          onClose();
        },
      },
    );
  }

  function handleClose() {
    setForm(EMPTY_PROPERTY_FORM);
    createMutation.reset();
    onClose();
  }

  return (
    <SlideOverShell isOpen={isOpen} onClose={handleClose} headingId={headingId} title="Add Property">
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
        <PropertyFormFields form={form} onChange={set} />
        {createMutation.isError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
            {extractErrorMessage(createMutation.error)}
          </p>
        )}
        <div className="pt-2">
          <SubmitButton isPending={createMutation.isPending} label="Create Property" pendingLabel="Creating..." />
        </div>
      </form>
    </SlideOverShell>
  );
}

// ── Edit Property Slide-over ─────────────────────────────────────────────────

function EditPropertySlideOver({
  isOpen,
  onClose,
  property,
}: {
  isOpen: boolean;
  onClose: () => void;
  property: Record<string, unknown>;
}) {
  const headingId = useId();
  const updateMutation = useUpdateProperty();
  const [form, setForm] = useState<PropertyFormData>(EMPTY_PROPERTY_FORM);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: (property.name as string) ?? '',
        city: (property.city as string) ?? '',
        address: (property.address as string) ?? '',
        country: (property.country as string) ?? '',
        description: (property.description as string) ?? '',
      });
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function set<K extends keyof PropertyFormData>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: UpdatePropertyPayload = {
      name: form.name.trim(),
      city: form.city.trim(),
      address: form.address.trim(),
      country: form.country.trim(),
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
    };
    updateMutation.mutate(
      { id: property.id as string, payload },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <SlideOverShell isOpen={isOpen} onClose={onClose} headingId={headingId} title="Edit Property">
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
        <PropertyFormFields form={form} onChange={set} />
        {updateMutation.isError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
            {extractErrorMessage(updateMutation.error)}
          </p>
        )}
        <div className="pt-2">
          <SubmitButton isPending={updateMutation.isPending} label="Save Changes" pendingLabel="Saving..." />
        </div>
      </form>
    </SlideOverShell>
  );
}

// ── Property Form Fields (shared between Add and Edit) ───────────────────────

function PropertyFormFields({
  form,
  onChange,
}: {
  form: PropertyFormData;
  onChange: <K extends keyof PropertyFormData>(key: K, value: string) => void;
}) {
  return (
    <>
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          Property Name <span aria-label="required">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="Sunset Apartments"
          required
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          Address <span aria-label="required">*</span>
        </label>
        <input
          type="text"
          value={form.address}
          onChange={(e) => onChange('address', e.target.value)}
          placeholder="123 Main Street"
          required
          className={INPUT_CLASS}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            City <span aria-label="required">*</span>
          </label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => onChange('city', e.target.value)}
            placeholder="London"
            required
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Country <span aria-label="required">*</span>
          </label>
          <input
            type="text"
            value={form.country}
            onChange={(e) => onChange('country', e.target.value)}
            placeholder="UK"
            required
            className={INPUT_CLASS}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          Description
        </label>
        <textarea
          rows={3}
          value={form.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Brief description of the property..."
          className={cn(INPUT_CLASS, 'resize-none')}
        />
      </div>
    </>
  );
}

// ── Add Unit Slide-over ──────────────────────────────────────────────────────

interface UnitFormData {
  name: string;
  type: string;
  capacity: string;
  basePrice: string;
  weekendPrice: string;
}

const EMPTY_UNIT_FORM: UnitFormData = {
  name: '',
  type: 'studio',
  capacity: '2',
  basePrice: '',
  weekendPrice: '',
};

function AddUnitSlideOver({
  isOpen,
  onClose,
  propertyId,
  propertyName,
}: {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
}) {
  const headingId = useId();
  const [form, setForm] = useState<UnitFormData>(EMPTY_UNIT_FORM);
  const createMutation = useCreateUnit();

  function set<K extends keyof UnitFormData>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate(
      {
        propertyId,
        payload: {
          name: form.name.trim(),
          type: form.type,
          capacity: parseInt(form.capacity, 10) || 2,
          basePrice: parseFloat(form.basePrice) || 0,
          ...(form.weekendPrice.trim() && parseFloat(form.weekendPrice) > 0
            ? { weekendPrice: parseFloat(form.weekendPrice) }
            : {}),
        },
      },
      {
        onSuccess: () => {
          setForm(EMPTY_UNIT_FORM);
          onClose();
        },
      },
    );
  }

  function handleClose() {
    setForm(EMPTY_UNIT_FORM);
    createMutation.reset();
    onClose();
  }

  return (
    <SlideOverShell isOpen={isOpen} onClose={handleClose} headingId={headingId} title="Add Unit" subtitle={propertyName}>
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
        <UnitFormFields form={form} onChange={set} />
        {createMutation.isError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
            {extractErrorMessage(createMutation.error)}
          </p>
        )}
        <div className="pt-2">
          <SubmitButton isPending={createMutation.isPending} label="Add Unit" pendingLabel="Creating..." />
        </div>
      </form>
    </SlideOverShell>
  );
}

// ── Edit Unit Slide-over ─────────────────────────────────────────────────────

function EditUnitSlideOver({
  isOpen,
  onClose,
  propertyId,
  propertyName,
  unit,
}: {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
  unit: Record<string, unknown>;
}) {
  const headingId = useId();
  const updateMutation = useUpdateUnit();
  const [form, setForm] = useState<UnitFormData>(EMPTY_UNIT_FORM);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: (unit.name as string) ?? '',
        type: (unit.type as string) ?? 'studio',
        capacity: String((unit.capacity as number) ?? 2),
        basePrice: String((unit.basePrice as number) ?? ''),
        weekendPrice: unit.weekendPrice != null ? String(unit.weekendPrice as number) : '',
      });
      setIsActive((unit.isActive as boolean) ?? true);
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function set<K extends keyof UnitFormData>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: UpdateUnitPayload = {
      name: form.name.trim(),
      type: form.type,
      capacity: parseInt(form.capacity, 10) || 2,
      basePrice: parseFloat(form.basePrice) || 0,
      ...(form.weekendPrice.trim() && parseFloat(form.weekendPrice) > 0
        ? { weekendPrice: parseFloat(form.weekendPrice) }
        : {}),
      isActive,
    };
    updateMutation.mutate(
      { propertyId, unitId: unit.id as string, payload },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <SlideOverShell isOpen={isOpen} onClose={onClose} headingId={headingId} title="Edit Unit" subtitle={propertyName}>
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
        <UnitFormFields form={form} onChange={set} />

        {/* Active / Inactive toggle */}
        <div className="flex items-center justify-between">
          <label htmlFor="unit-active-toggle" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Active
          </label>
          <button
            id="unit-active-toggle"
            type="button"
            role="switch"
            aria-checked={isActive}
            onClick={() => setIsActive((v) => !v)}
            className={cn(
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              isActive ? 'bg-primary-600' : 'bg-gray-200',
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform',
                isActive ? 'translate-x-5' : 'translate-x-0',
              )}
            />
          </button>
        </div>

        {updateMutation.isError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
            {extractErrorMessage(updateMutation.error)}
          </p>
        )}
        <div className="pt-2">
          <SubmitButton isPending={updateMutation.isPending} label="Save Changes" pendingLabel="Saving..." />
        </div>
      </form>
    </SlideOverShell>
  );
}

// ── Unit Form Fields (shared between Add and Edit) ───────────────────────────

function UnitFormFields({
  form,
  onChange,
}: {
  form: UnitFormData;
  onChange: <K extends keyof UnitFormData>(key: K, value: string) => void;
}) {
  return (
    <>
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          Unit Name <span aria-label="required">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="e.g. Apartment 101"
          required
          className={INPUT_CLASS}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          Unit Type <span aria-label="required">*</span>
        </label>
        <div className="relative">
          <select
            value={form.type}
            onChange={(e) => onChange('type', e.target.value)}
            required
            className={cn(INPUT_CLASS, 'appearance-none pr-8 bg-white')}
          >
            {UNIT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Max Guests <span aria-label="required">*</span>
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={form.capacity}
            onChange={(e) => onChange('capacity', e.target.value)}
            required
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            Base Price / Night <span aria-label="required">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
              $
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.basePrice}
              onChange={(e) => onChange('basePrice', e.target.value)}
              placeholder="0.00"
              required
              className={cn(INPUT_CLASS, 'pl-7')}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          Weekend Price / Night
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
            $
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.weekendPrice}
            onChange={(e) => onChange('weekendPrice', e.target.value)}
            placeholder="Same as base price if empty"
            className={cn(INPUT_CLASS, 'pl-7')}
          />
        </div>
      </div>
    </>
  );
}

// ── Submit Button ────────────────────────────────────────────────────────────

function SubmitButton({
  isPending,
  label,
  pendingLabel,
}: {
  isPending: boolean;
  label: string;
  pendingLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={isPending}
      className={cn(
        'w-full px-4 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        isPending ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700',
      )}
    >
      {isPending ? (
        <span className="flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
          {pendingLabel}
        </span>
      ) : (
        label
      )}
    </button>
  );
}

// ── Unit Type Label ──────────────────────────────────────────────────────────

function unitTypeLabel(type: string): string {
  return UNIT_TYPES.find((t) => t.value === type)?.label ?? type;
}

// ── Property Row with expandable units ───────────────────────────────────────

function PropertyRow({
  property,
  onAddUnit,
  onEditProperty,
  onDeleteProperty,
  onEditUnit,
  onDeleteUnit,
  isAdmin,
  canEditListing,
  canViewListing,
  onOpenListing,
}: {
  property: Record<string, unknown>;
  onAddUnit: (propertyId: string, propertyName: string) => void;
  onEditProperty: (property: Record<string, unknown>) => void;
  onDeleteProperty: (propertyId: string, propertyName: string) => void;
  onEditUnit: (propertyId: string, propertyName: string, unit: Record<string, unknown>) => void;
  onDeleteUnit: (propertyId: string, unitId: string, unitName: string) => void;
  isAdmin: boolean;
  /** F1/F10: ADMIN/OWNER → Edit listing; REVENUE_MANAGER → View listing. */
  canEditListing: boolean;
  canViewListing: boolean;
  onOpenListing: (propertyId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const propertyName = (property.name as string) ?? '';
  const propertyId = (property.id as string) ?? '';
  const updateHousekeeping = useUpdateHousekeepingStatus();

  const { data: unitsData, isLoading: unitsLoading } = useUnits(expanded ? propertyId : undefined);

  const units: Record<string, unknown>[] = useMemo(() => {
    if (Array.isArray(unitsData)) return unitsData as Record<string, unknown>[];
    return [];
  }, [unitsData]);

  const unitCount = units.length || (property.unitCount as number) || 0;

  return (
    <>
      {/* Property row */}
      <tr className="hover:bg-gray-50 transition-colors group">
        <td className="px-5 py-4">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${propertyName}`}
            className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg"
          >
            <div
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary-100 shrink-0"
              aria-hidden="true"
            >
              <Building2 size={16} className="text-primary-600" />
            </div>
            <span className="text-sm font-semibold text-gray-900">{propertyName}</span>
            <ChevronRight
              size={14}
              className={cn(
                'text-gray-400 transition-transform duration-200',
                expanded ? 'rotate-90' : '',
              )}
              aria-hidden="true"
            />
          </button>
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <MapPin size={13} className="text-gray-400 shrink-0" aria-hidden="true" />
            {property.address as string}
          </div>
        </td>
        <td className="px-5 py-4 text-sm text-gray-600">
          {property.city as string}
        </td>
        <td className="px-5 py-4">
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <Home size={13} className="text-gray-400 shrink-0" aria-hidden="true" />
            <span>
              {unitCount} {unitCount === 1 ? 'unit' : 'units'}
            </span>
          </div>
        </td>
        <td className="px-5 py-4 text-right">
          <div className="inline-flex items-center gap-1.5">
            {canViewListing && (
              <button
                type="button"
                onClick={() => onOpenListing(propertyId)}
                aria-label={`${canEditListing ? 'Edit' : 'View'} listing for ${propertyName}`}
                className={cn(
                  'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  canEditListing
                    ? 'border-primary-200 text-primary-700 hover:bg-primary-50'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                )}
              >
                {canEditListing ? (
                  <SquarePen size={12} aria-hidden="true" />
                ) : (
                  <Eye size={12} aria-hidden="true" />
                )}
                {canEditListing ? 'Edit listing' : 'View listing'}
              </button>
            )}
          </div>
          <div className="inline-flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {isAdmin && (
              <>
                <button
                  type="button"
                  onClick={() => onEditProperty(property)}
                  aria-label={`Edit ${propertyName}`}
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600',
                    'hover:bg-gray-50 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  )}
                >
                  <Pencil size={12} aria-hidden="true" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteProperty(propertyId, propertyName)}
                  aria-label={`Delete ${propertyName}`}
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-600',
                    'hover:bg-red-50 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
                  )}
                >
                  <Trash2 size={12} aria-hidden="true" />
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => onAddUnit(propertyId, propertyName)}
                  aria-label={`Add unit to ${propertyName}`}
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-primary-200 text-xs font-medium text-primary-600',
                    'hover:bg-primary-50 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  )}
                >
                  <Plus size={12} aria-hidden="true" />
                  Add Unit
                </button>
              </>
            )}
          </div>
        </td>
      </tr>

      {/* Expanded units section */}
      {expanded && (
        <tr>
          <td colSpan={5} className="px-0 py-0">
            <div className="bg-gray-50 border-t border-b border-gray-100 px-5 py-3">
              {unitsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 size={20} className="animate-spin text-primary-500" aria-hidden="true" />
                  <span className="sr-only">Loading units...</span>
                </div>
              ) : units.length === 0 ? (
                <div className="flex items-center gap-3 py-4 text-sm text-gray-400">
                  <Home size={16} className="text-gray-300" aria-hidden="true" />
                  No units yet.
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => onAddUnit(propertyId, propertyName)}
                      className="text-primary-600 hover:text-primary-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                    >
                      Add the first unit
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {units.map((unit) => {
                    const hkStatus = (unit.housekeepingStatus as string) ?? 'clean';
                    const basePrice = unit.basePrice as number | string | undefined;
                    const priceStr = basePrice != null
                      ? `$${parseFloat(String(basePrice)).toLocaleString()}/night`
                      : null;
                    const weekendPrice = unit.weekendPrice as number | undefined;
                    const basePriceNum = basePrice != null ? parseFloat(String(basePrice)) : undefined;
                    const unitId = unit.id as string;
                    const unitName = unit.name as string;
                    const unitIsActive = (unit.isActive as boolean) ?? true;

                    return (
                      <div
                        key={unitId}
                        className={cn(
                          'bg-white rounded-lg border border-gray-200 p-3.5 flex flex-col gap-2.5',
                          !unitIsActive && 'opacity-60',
                        )}
                        aria-label={`Unit ${unitName}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {unitName}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {unitTypeLabel((unit.type as string) ?? '')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {!unitIsActive && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                Inactive
                              </span>
                            )}
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                HOUSEKEEPING_STATUS_STYLES[hkStatus] ?? 'bg-gray-100 text-gray-600',
                              )}
                            >
                              {HOUSEKEEPING_STATUS_LABELS[hkStatus] ?? hkStatus}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Bed size={11} aria-hidden="true" />
                            Up to {(unit.capacity as number) ?? '?'} guests
                          </span>
                          {priceStr && (
                            <span className="flex items-center gap-1">
                              <DollarSign size={11} aria-hidden="true" />
                              {priceStr}
                              {weekendPrice != null && basePriceNum != null && weekendPrice !== basePriceNum && (
                                <span className="text-xs text-gray-400 ml-1">(${weekendPrice.toLocaleString()} weekends)</span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Actions row */}
                        <div className="flex items-center gap-1.5 pt-0.5 flex-wrap">
                          {/* Housekeeping transition button */}
                          {(() => {
                            const transition = HOUSEKEEPING_TRANSITIONS[hkStatus];
                            if (!transition) return null;
                            const isMutating =
                              updateHousekeeping.isPending &&
                              (updateHousekeeping.variables as { unitId: string } | undefined)?.unitId === unitId;
                            return (
                              <button
                                type="button"
                                disabled={isMutating}
                                onClick={() =>
                                  updateHousekeeping.mutate({ unitId, status: transition.next })
                                }
                                aria-label={`${transition.label} for ${unitName}`}
                                className={cn(
                                  'text-xs px-2 py-1 rounded border font-medium transition-colors',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                  isMutating && 'opacity-50 cursor-not-allowed',
                                  TRANSITION_BUTTON_STYLES[transition.next] ?? 'border-gray-200 text-gray-600 hover:bg-gray-50',
                                )}
                              >
                                {isMutating ? (
                                  <span className="flex items-center gap-1">
                                    <Loader2 size={10} className="animate-spin" aria-hidden="true" />
                                    Updating...
                                  </span>
                                ) : (
                                  transition.label
                                )}
                              </button>
                            );
                          })()}

                          {/* Edit / Delete unit buttons (admin only) */}
                          {isAdmin && (
                            <>
                              <button
                                type="button"
                                onClick={() => onEditUnit(propertyId, propertyName, unit)}
                                aria-label={`Edit unit ${unitName}`}
                                className={cn(
                                  'text-xs px-2 py-1 rounded border border-gray-200 font-medium text-gray-600',
                                  'hover:bg-gray-50 transition-colors',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                )}
                              >
                                <Pencil size={10} className="inline-block mr-1" aria-hidden="true" />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => onDeleteUnit(propertyId, unitId, unitName)}
                                aria-label={`Delete unit ${unitName}`}
                                className={cn(
                                  'text-xs px-2 py-1 rounded border border-red-200 font-medium text-red-600',
                                  'hover:bg-red-50 transition-colors',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
                                )}
                              >
                                <Trash2 size={10} className="inline-block mr-1" aria-hidden="true" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Review / Approval section (operator side) ────────────────────────────────
// Surfaces each property's BestAcco review status, shows reviewer feedback when
// changes are requested, and lets the operator submit drafts for review.

function ReviewSection({ isAdmin }: { isAdmin: boolean }) {
  const { data: status, isLoading } = useOnboardingStatus();
  const submitMutation = useSubmitForReview();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const properties = status?.properties ?? [];

  if (isLoading || properties.length === 0) return null;

  // Submittable = draft or changes_requested (matches backend submit-for-review).
  const submittable = properties.filter(
    (p) =>
      p.reviewStatus === PropertyReviewStatus.DRAFT ||
      p.reviewStatus === PropertyReviewStatus.CHANGES_REQUESTED,
  );
  const changesRequested = properties.filter(
    (p) => p.reviewStatus === PropertyReviewStatus.CHANGES_REQUESTED,
  );

  function handleSubmit() {
    setSubmitError(null);
    setJustSubmitted(false);
    submitMutation.mutate(undefined, {
      onSuccess: () => setJustSubmitted(true),
      onError: (err) => setSubmitError(extractErrorMessage(err)),
    });
  }

  return (
    <section
      aria-labelledby="review-status-heading"
      className="rounded-xl border border-gray-200 bg-white shadow-sm p-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 id="review-status-heading" className="text-sm font-semibold text-gray-900">
            Marketplace review status
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            Properties must be approved by BestAcco before they appear on the public booking site.
          </p>
        </div>
        {isAdmin && submittable.length > 0 && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitMutation.isPending}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              submitMutation.isPending ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700',
            )}
          >
            {submitMutation.isPending ? (
              <>
                <Spinner size={15} className="animate-spin" aria-hidden="true" />
                Submitting…
              </>
            ) : (
              <>
                <Send size={15} aria-hidden="true" />
                Submit {submittable.length} for review
              </>
            )}
          </button>
        )}
      </div>

      {justSubmitted && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700" role="status">
          <CheckCircle2 size={15} aria-hidden="true" />
          Submitted for review. BestAcco will respond shortly.
        </div>
      )}
      {submitError && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert">
          {submitError}
        </div>
      )}

      {/* Per-property status list */}
      <ul className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
        {properties.map((p) => (
          <li key={p.id} className="py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-gray-800">{p.name}</span>
              <ReviewStatusBadge status={p.reviewStatus} />
            </div>
            {p.reviewStatus === PropertyReviewStatus.CHANGES_REQUESTED && p.reviewFeedback && (
              <div className="mt-2 flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
                <MessageSquareWarning size={15} className="text-orange-500 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="text-xs font-semibold text-orange-800 uppercase tracking-wide">Changes requested</p>
                  <p className="text-sm text-orange-900 whitespace-pre-wrap">{p.reviewFeedback}</p>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      {changesRequested.length > 0 && isAdmin && (
        <p className="mt-3 text-xs text-gray-500">
          Address the feedback above, then re-submit for review.
        </p>
      )}
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PropertiesPage() {
  const [search, setSearch] = useState('');
  const [isAddPropertyOpen, setIsAddPropertyOpen] = useState(false);
  const [addUnitTarget, setAddUnitTarget] = useState<{ id: string; name: string } | null>(null);
  const [editPropertyTarget, setEditPropertyTarget] = useState<Record<string, unknown> | null>(null);
  const [editUnitTarget, setEditUnitTarget] = useState<{
    propertyId: string;
    propertyName: string;
    unit: Record<string, unknown>;
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;
  // Property-team self-service editing: the listing is now editable in-place via
  // the dedicated Listing Editor (`/properties/:id/listing`). ADMIN/OWNER edit;
  // REVENUE_MANAGER views read-only; OPERATIONS gets no entry (server also guards).
  const canEditListing = user?.role === UserRole.ADMIN || user?.role === UserRole.OWNER;
  const canViewListing = canEditListing || user?.role === UserRole.REVENUE_MANAGER;
  // The operational property/unit CRUD (Add Property, Edit/Delete, Add Unit) stays
  // a BestAcco-console concern — kept off this operational table.
  const listingManagedByBestAcco = true;
  const canManageListing = isAdmin && !listingManagedByBestAcco;

  function openListing(propertyId: string) {
    navigate(`/properties/${propertyId}/listing?tab=overview`);
  }

  const { data, isLoading, error } = useProperties();
  const deletePropertyMutation = useDeleteProperty();
  const deleteUnitMutation = useDeleteUnit();

  const properties: Array<Record<string, unknown>> = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ?? []);
  }, [data]);

  const totalUnits = useMemo(
    () =>
      properties.reduce((acc: number, p: Record<string, unknown>) => {
        const units = Array.isArray(p.units) ? p.units.length : (p.unitCount as number ?? 0);
        return acc + units;
      }, 0),
    [properties],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter(
      (p) =>
        ((p.name as string) ?? '').toLowerCase().includes(q) ||
        ((p.city as string) ?? '').toLowerCase().includes(q) ||
        ((p.address as string) ?? '').toLowerCase().includes(q),
    );
  }, [search, properties]);

  const ownerId =
    (properties[0]?.ownerId as string) ??
    (user?.id as string) ??
    '';

  function handleDeleteProperty(propertyId: string, propertyName: string) {
    setDeleteError(null);
    if (!window.confirm(`Delete "${propertyName}"? This cannot be undone.`)) return;
    deletePropertyMutation.mutate(propertyId, {
      onError: (err) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to delete property. It may have active bookings.';
        setDeleteError(message);
      },
    });
  }

  function handleDeleteUnit(propertyId: string, unitId: string, unitName: string) {
    setDeleteError(null);
    if (!window.confirm(`Delete unit "${unitName}"? This cannot be undone.`)) return;
    deleteUnitMutation.mutate(
      { propertyId, unitId },
      {
        onError: (err) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'Failed to delete unit. It may have active bookings.';
          setDeleteError(message);
        },
      },
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Properties</h1>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700">Failed to load properties. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Properties</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {properties.length} {properties.length === 1 ? 'property' : 'properties'} &middot;{' '}
            {totalUnits} total {totalUnits === 1 ? 'unit' : 'units'}
          </p>
        </div>

        {canManageListing && (
          <button
            type="button"
            onClick={() => setIsAddPropertyOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Plus size={16} aria-hidden="true" />
            Add Property
          </button>
        )}
      </div>

      {/* Self-service listing editing entry point. */}
      {canViewListing && (
        <div
          className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
          role="status"
        >
          <Info size={16} className="text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-blue-800">
            {canEditListing
              ? 'Edit your listing content (photos, rooms & rates, amenities, policies and profile) directly — choose Edit listing on a property. Changes go live on the storefront immediately.'
              : 'You can view listing content (read-only) — choose View listing on a property. Ask an Administrator to make changes.'}
          </p>
        </div>
      )}

      {/* Delete error banner */}
      {deleteError && (
        <div
          className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="text-sm text-red-700">{deleteError}</p>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            aria-label="Dismiss error"
            className="p-1 rounded text-red-400 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Marketplace review status + submit-for-review */}
      <ReviewSection isAdmin={isAdmin} />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search properties..."
          aria-label="Search properties"
          className={cn(
            'w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 bg-white',
            'placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          )}
        />
      </div>

      <p className="sr-only" aria-live="polite">
        {filtered.length} {filtered.length === 1 ? 'property' : 'properties'} found
      </p>

      {/* Properties table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-primary-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading properties...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-100"
              aria-label="Properties list"
            >
              <thead>
                <tr className="bg-gray-50">
                  {['Property Name', 'Address', 'City', 'Units', ''].map((col) => (
                    <th
                      key={col}
                      scope="col"
                      className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-5 py-12 text-center text-sm text-gray-400"
                    >
                      No properties match your search.
                    </td>
                  </tr>
                ) : (
                  filtered.map((property) => (
                    <PropertyRow
                      key={property.id as string}
                      property={property}
                      onAddUnit={(id, name) => setAddUnitTarget({ id, name })}
                      onEditProperty={(p) => setEditPropertyTarget(p)}
                      onDeleteProperty={handleDeleteProperty}
                      onEditUnit={(pid, pname, unit) =>
                        setEditUnitTarget({ propertyId: pid, propertyName: pname, unit })
                      }
                      onDeleteUnit={handleDeleteUnit}
                      isAdmin={canManageListing}
                      canEditListing={canEditListing}
                      canViewListing={canViewListing}
                      onOpenListing={openListing}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Property slide-over */}
      <AddPropertySlideOver
        isOpen={isAddPropertyOpen}
        onClose={() => setIsAddPropertyOpen(false)}
        ownerId={ownerId}
      />

      {/* Edit Property slide-over */}
      {editPropertyTarget && (
        <EditPropertySlideOver
          isOpen={true}
          onClose={() => setEditPropertyTarget(null)}
          property={editPropertyTarget}
        />
      )}

      {/* Add Unit slide-over */}
      {addUnitTarget && (
        <AddUnitSlideOver
          isOpen={true}
          onClose={() => setAddUnitTarget(null)}
          propertyId={addUnitTarget.id}
          propertyName={addUnitTarget.name}
        />
      )}

      {/* Edit Unit slide-over */}
      {editUnitTarget && (
        <EditUnitSlideOver
          isOpen={true}
          onClose={() => setEditUnitTarget(null)}
          propertyId={editUnitTarget.propertyId}
          propertyName={editUnitTarget.propertyName}
          unit={editUnitTarget.unit}
        />
      )}
    </div>
  );
}
